import React, { useState, useEffect } from "react";

import { Trans } from "react-i18next";
import { Callout, Switch, MenuItem, Button, FormGroup, NumericInput, HTMLTable, InputGroup } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";

import { authenticatedGetFetch, authenticatedPostFetch } from "../../utils/AuthenticatedFetch";

export function getSetting(key, default_value) {
  try {
    const value = JSON.parse(localStorage.getItem("server_settings"))[key];
    return (value !== undefined ? value : default_value);
  } catch {
    return default_value;
  }
}

function setSetting(key, value) {
  const settings = JSON.parse(localStorage.getItem("server_settings"));
  settings[key] = value;
  localStorage.setItem("server_settings", JSON.stringify(settings));
}

function BooleanSetting(props) {
  const [loading, setLoading] = useState(false);
  return (
    <tr>
      <td>
        <FormGroup
          label={<Trans>{props.label}</Trans>}
          labelFor={props.label}
          inline={true}
        // labelInfo or helperText can be used to display more info
        />
      </td>
      <td>
        <Switch
          id={props.label}
          checked={props.value.value}
          disabled={loading}
          onChange={(cb) => {
            setLoading(true);
            setSetting(props.label, cb.target.checked);
            authenticatedPostFetch("/setting", { key: props.label, value: cb.target.checked }, (code) => {
              if (code === 202) {
                props.onValueChange();
              }
              setLoading(false);
            })
          }} />
      </td>
    </tr>
  );
}

function NumberSetting(props) {
  const [loading, setLoading] = useState(false);
  const [valueTmp, setValueTmp] = useState();

  useEffect(() => {
    if (valueTmp !== undefined) {
      const delayDebounceFn = setTimeout(() => {
        setLoading(true);
        setSetting(props.label, valueTmp);
        authenticatedPostFetch("/setting", { key: props.label, value: valueTmp }, (code) => {
          if (code === 202) {
            props.onValueChange();
          }
          setLoading(false);
          setValueTmp();
        })
      }, 3000);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [valueTmp, props]);

  return (
    <tr>
      <td>
        <FormGroup
          label={<Trans>{props.label}</Trans>}
          labelFor={props.label}
          inline={true}
        // labelInfo or helperText can be used to display more info
        />
      </td>
      <td>
        <NumericInput
          id={props.label}
          min={props.min}
          max={props.max}
          minorStepSize={1} /* If minorStepSize is non-null, the number of decimal places in that value will be the maximum precision. */
          loading={loading}
          value={valueTmp !== undefined ? valueTmp : props.value.value}
          onValueChange={(valueAsNumber) => {
            if (valueAsNumber >= props.min && valueAsNumber <= props.max)
              setValueTmp(valueAsNumber);
          }}
        />
      </td>
    </tr>
  );
}

function EnumSetting(props) {
  const [loading, setLoading] = useState(false);
  return (<tr>
    <td>
      <FormGroup
        label={<Trans>{props.label}</Trans>}
        labelFor={props.label}
        inline={true}
      // labelInfo or helperText can be used to display more info
      />
    </td>
    <td><Select
      id={props.label}
      filterable={false}
      items={props.value.values}
      itemRenderer={(item, { select, modifiers }) => {
        return <MenuItem
          key={item.value}
          label={item.value}
          text={item.description}
          disabled={loading}
          onClick={() => {
            setLoading(true);
            authenticatedPostFetch("/setting", { key: props.label, value: item.value }, (code) => {
              if (code === 202) {
                setSetting(props.label, item.value);
                props.onValueChange();
              }
              setLoading(false);
            })
          }}
          selected={item.value === props.value.value}
        />
      }}
    >
      <Button text={props.value.value} rightIcon="double-caret-vertical" />
    </Select></td>
  </tr>);
}

function StringSetting(props) {
  const [loading, setLoading] = useState(false);
  const [valueTmp, setValueTmp] = useState();

  useEffect(() => {
    if (valueTmp !== undefined) {
      const delayDebounceFn = setTimeout(() => {
        setLoading(true);
        setSetting(props.label, valueTmp);
        authenticatedPostFetch("/setting", { key: props.label, value: valueTmp }, (code) => {
          if (code === 202) {
            props.onValueChange();
          }
          setLoading(false);
          setValueTmp();
        })
      }, 3000);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [valueTmp, props]);

  return (
    <tr>
      <td>
        <FormGroup
          label={<Trans>{props.label}</Trans>}
          labelFor={props.label}
          inline={true}
        // labelInfo or helperText can be used to display more info
        />
      </td>
      <td>
        <InputGroup
          id={props.label}
          disabled={loading}
          value={valueTmp !== undefined ? valueTmp : props.value.value}
          onChange={(val) => setValueTmp(val.target.value)}
        />
      </td>
    </tr>
  );
}


export default class InstallationSettings extends React.Component {
  constructor(props) {
    super();
    this.onValueChange = this.onValueChange.bind(this);
    this.state = { settings: [] };
  }

  onValueChange() {
    if (localStorage.getItem("perm_superadmin") === "true")
      authenticatedGetFetch('/settings',
        (response) => {
          this.setState({ settings: response });
        });
  }

  componentDidMount() {
    this.onValueChange();
  }

  render() {
    let ret = [];
    for (const key of Object.keys(this.state.settings)) {
      if (typeof this.state.settings[key].value == "boolean") // display a on/off switch
        ret.push(<BooleanSetting
          label={key}
          key={key}
          id={this.state.settings[key].id}
          value={this.state.settings[key]}
          onValueChange={this.onValueChange}
        />);
      else if (this.state.settings[key]["min"] !== undefined)
        ret.push(<NumberSetting
          label={key}
          key={key}
          id={this.state.settings[key].id}
          value={this.state.settings[key]}
          onValueChange={this.onValueChange}
          min={this.state.settings[key].min}
          max={this.state.settings[key].max}
        />);
      else if (this.state.settings[key]["values"] !== undefined) // display a select form
        ret.push(<EnumSetting
          label={key}
          key={key}
          id={this.state.settings[key].id}
          value={this.state.settings[key]}
          onValueChange={this.onValueChange}
        />);
      else if (this.state.settings[key]["min"] !== undefined)
        ret.push(<NumberSetting
          label={key}
          key={key}
          id={this.state.settings[key].id}
          value={this.state.settings[key]}
          onValueChange={this.onValueChange}
          min={this.state.settings[key].min}
          max={this.state.settings[key].max}
        />);
      else if (typeof this.state.settings[key].value == "string")
        ret.push(<StringSetting
          label={key}
          key={key}
          id={this.state.settings[key].id}
          value={this.state.settings[key]}
          onValueChange={this.onValueChange}
        />);
    }

    // order settings by id
    ret = [...ret].sort((a, b) => {
      return a.props.id > b.props.id ? 1 : -1;
    });

    return (<Callout title={<Trans>installation settings</Trans>}>
      <Callout intent="warning"><Trans>change of these settings will be effective at next login for all users</Trans></Callout>
      <HTMLTable striped={true} compact={true}>
        <tbody>{ret}</tbody>
      </HTMLTable>
    </Callout>);
  }
}
